import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTabNavigationStore } from '../../store/tabNavigationStore'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { PageHeader } from '../../components/ui/PageHeader'
import { paymentSimpleApi } from '../../api/paymentSimple'
import { athletesApi } from '../../api/athletes'
import { RecordPaymentSheet } from '../Sheets/RecordPaymentSheet'
import {
  formatBalance,
  formatAmount,
  PAYMENT_METHOD_LABELS,
} from '../../types/payment_simple'

export function AthletePaymentsScreen() {
  const { getCurrentScreen, popScreen } = useTabNavigationStore()
  const currentScreen = getCurrentScreen()
  const athleteId = currentScreen.params?.athleteId as string
  const groupId = currentScreen.params?.groupId as string

  const [showRecordSheet, setShowRecordSheet] = useState(false)
  const queryClient = useQueryClient()

  const { data: athlete } = useQuery({
    queryKey: ['athlete', athleteId],
    queryFn: () => athletesApi.getById(athleteId),
    enabled: !!athleteId,
  })

  const { data: balance, isLoading: balanceLoading } = useQuery({
    queryKey: ['athlete-balance', athleteId],
    queryFn: () => paymentSimpleApi.getAthleteBalance(athleteId),
    enabled: !!athleteId,
    retry: false,
  })

  const { data: transactions, isLoading: transactionsLoading } = useQuery({
    queryKey: ['athlete-transactions', athleteId],
    queryFn: () => paymentSimpleApi.getAthleteTransactions(athleteId),
    enabled: !!athleteId,
  })

  const deleteTransactionMutation = useMutation({
    mutationFn: (transactionId: string) => paymentSimpleApi.deleteTransaction(transactionId),
    onSuccess: () => {
      // Invalidate both transactions and balance queries
      queryClient.invalidateQueries({ queryKey: ['athlete-transactions', athleteId] })
      queryClient.invalidateQueries({ queryKey: ['athlete-balance', athleteId] })
    },
  })

  const handleDeleteTransaction = (transactionId: string, amount: number) => {
    const confirm = window.confirm(
      `Удалить платёж на ${formatAmount(amount)}?\n\nБаланс будет пересчитан автоматически.`
    )

    if (!confirm) return

    try {
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('warning')
    } catch {}

    deleteTransactionMutation.mutate(transactionId, {
      onSuccess: () => {
        try {
          window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success')
        } catch {}
      },
    })
  }

  if (balanceLoading || transactionsLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p style={{ color: 'var(--text-hint)' }}>Загрузка...</p>
      </div>
    )
  }

  return (
    <div className="p-4 space-y-4 pb-24">
      <PageHeader title={athlete?.name || 'Оплаты спортсмена'} onBackClick={popScreen} />

      {/* Текущий баланс */}
      {balance && (
        <Card>
          <div className="text-center py-4">
            <p className="text-sm mb-2" style={{ color: 'var(--text-hint)' }}>
              Текущий баланс
            </p>
            <p
              className="text-3xl font-bold mb-1"
              style={{ color: balance.balance >= 0 ? 'var(--text-primary)' : 'var(--danger)' }}
            >
              {formatBalance(balance.balance)}
            </p>
            <p className="text-xs" style={{ color: 'var(--text-hint)' }}>
              {balance.balance > 0
                ? 'Переплата (аванс)'
                : balance.balance < 0
                ? 'Задолженность'
                : 'Баланс в норме'}
            </p>
          </div>
        </Card>
      )}

      {/* Кнопка записать платёж */}
      <Button onClick={() => setShowRecordSheet(true)} fullWidth>
        Записать платёж
      </Button>

      {/* История транзакций */}
      <div>
        <h3 className="text-[17px] font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
          История платежей
        </h3>

        {transactions && transactions.length > 0 ? (
          <Card>
            <div className="space-y-1">
              {transactions.map((tx) => (
                <div
                  key={tx.id}
                  className="py-3 px-2"
                  style={{ borderBottom: '1px solid var(--separator)' }}
                >
                  <div className="flex items-start justify-between mb-1">
                    <div className="flex-1">
                      <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {formatAmount(tx.amount)}
                      </p>
                      {tx.description && (
                        <p className="text-xs mt-1" style={{ color: 'var(--text-hint)' }}>
                          {tx.description}
                        </p>
                      )}
                    </div>
                    <div className="text-right ml-3 flex flex-col items-end">
                      <p className="text-xs" style={{ color: 'var(--text-hint)' }}>
                        {new Date(tx.transaction_date).toLocaleDateString('ru-RU')}
                      </p>
                      {tx.payment_method && (
                        <p className="text-xs mt-1" style={{ color: 'var(--text-hint)' }}>
                          {PAYMENT_METHOD_LABELS[tx.payment_method]}
                        </p>
                      )}
                      <button
                        onClick={() => handleDeleteTransaction(tx.id, tx.amount)}
                        className="text-xs mt-2 px-2 py-1 rounded transition-opacity active:opacity-60"
                        style={{ color: 'var(--danger)', border: '1px solid var(--danger)' }}
                        disabled={deleteTransactionMutation.isPending}
                      >
                        {deleteTransactionMutation.isPending ? 'Удаление...' : 'Удалить'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ) : (
          <Card>
            <p className="text-center py-8" style={{ color: 'var(--text-hint)' }}>
              Нет платежей
            </p>
          </Card>
        )}
      </div>

      {/* Bottom sheet для записи платежа */}
      <RecordPaymentSheet
        isOpen={showRecordSheet}
        onClose={() => setShowRecordSheet(false)}
        athleteId={athleteId}
        athleteName={athlete?.name || ''}
        groupId={groupId}
      />
    </div>
  )
}
